import React, { useState, useEffect, useMemo } from "react";
import {
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Bar,
  ComposedChart,
} from "recharts";
import {
  Typography,
  Box,
  Autocomplete,
  TextField,
  CircularProgress,
  Grid,
  useTheme,
  useMediaQuery,
  alpha,
  Chip,
  Card,
  MenuItem,
} from "@mui/material";
import FilterListIcon from "@mui/icons-material/FilterList";
import StorefrontIcon from "@mui/icons-material/Storefront";
import DateRangeIcon from "@mui/icons-material/DateRange";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import useParsedTSVCache from "../hooks/useParsedTSVCache";

const TIME_FILTER_OPTIONS = [
  { label: "16 Weeks", value: "16_WEEKS" },
  { label: "30 Weeks", value: "30_WEEKS" },
  { label: "52 Weeks", value: "52_WEEKS" },
  { label: "All Time", value: "ALL_TIME" },
];

const MarketsDataByWeek = ({
  title = "Weekly Market Trends",
  subtitle = "",
  initialItemType = null,
  initialTimeFilter = null,
  onFilterChange = () => {},
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"), { noSsr: true }); // < 600px
  const [data, setData] = useState([]);
  const [itemTypes, setItemTypes] = useState([]);
  const [selectedItemType, setSelectedItemType] = useState("");
  const [error, setError] = useState(null);
  const [isHydrated, setIsHydrated] = useState(false);

  // Use parsed TSV cache - data is automatically parsed
  const {
    data: parsedData,
    loading: isLoading,
    error: fetchError,
  } = useParsedTSVCache(
    "markets_cache_all_time_by_week",
    "/markets-data/latest-all-time-by-week.tsv",
  );

  // Always start with default time filter to match SSR
  const [timeFilter, setTimeFilter] = useState(TIME_FILTER_OPTIONS[0]);

  // Mark component as hydrated after first render
  useEffect(() => {
    setIsHydrated(true);
  }, []);

  // Initialize time filter from URL after hydration completes
  useEffect(() => {
    if (!isHydrated) return;

    if (initialTimeFilter) {
      const found = TIME_FILTER_OPTIONS.find(
        (opt) => opt.value.toLowerCase() === initialTimeFilter.toLowerCase(),
      );
      if (found) {
        setTimeFilter(found);
      }
    }
  }, [isHydrated, initialTimeFilter]);

  // Process parsed data when it's loaded
  useEffect(() => {
    if (fetchError) {
      setError(`Error fetching data: ${fetchError}`);
      return;
    }

    if (parsedData && !isLoading) {
      // Extract unique item types and format as objects
      const uniqueTypes = [...new Set(parsedData.map((item) => item.item_type))]
        .filter(Boolean)
        .sort();
      const formattedTypes = uniqueTypes.map((type) => ({ label: type }));
      setItemTypes(formattedTypes);

      // Set initial selected item type from URL or default to DIAMOND
      if (formattedTypes.length > 0) {
        let initialItem = null;

        // Try to find item from URL parameter
        if (initialItemType) {
          initialItem = formattedTypes.find((t) => t.label === initialItemType);
        }

        // Fallback to DIAMOND or first available
        if (!initialItem) {
          const diamondOption = formattedTypes.find(
            (t) => t.label === "DIAMOND",
          );
          initialItem = diamondOption || formattedTypes[0];
        }

        setSelectedItemType(initialItem);
      }

      setData(parsedData);
    }
  }, [parsedData, isLoading, fetchError, initialItemType]);

  // Module-level cache for ISO week parsing (survives component re-renders)
  const weekDateCache = useMemo(() => new Map(), []);

  // Helper function to parse ISO week format (YYYY-WNN) with caching
  const parseISOWeek = useMemo(
    () => (yearWeek) => {
      if (!yearWeek) return null;

      // Check cache first
      if (weekDateCache.has(yearWeek)) {
        return weekDateCache.get(yearWeek);
      }

      const parts = yearWeek.split("-W");
      if (parts.length !== 2) return null;

      const year = parseInt(parts[0]);
      const week = parseInt(parts[1]);

      // Calculate date from ISO week number
      // ISO week 1 is the week with the first Thursday of the year
      const jan4 = new Date(year, 0, 4); // January 4th is always in week 1
      const dayOffset = (week - 1) * 7;
      const weekStart = new Date(jan4);
      weekStart.setDate(jan4.getDate() - jan4.getDay() + 1 + dayOffset); // Monday of the week

      // Cache the result
      weekDateCache.set(yearWeek, weekStart);

      return weekStart;
    },
    [weekDateCache],
  );

  // Memoize cutoff date calculation - only recalculate when time filter or data changes
  const cutoffDate = useMemo(() => {
    if (timeFilter.value === "ALL_TIME" || data.length === 0) return null;

    const weeksAgo = parseInt(timeFilter.value.split("_")[0]);

    const dates = data
      .map((item) => parseISOWeek(item["year-week"]))
      .filter(Boolean);

    if (dates.length === 0) return null;

    const maxDate = new Date(Math.max(...dates));
    const cutoff = new Date(maxDate);
    cutoff.setDate(cutoff.getDate() - weeksAgo * 7);

    return cutoff;
  }, [data, timeFilter, parseISOWeek]);

  // Memoize filtered data - only recalculate when dependencies actually change
  const filteredData = useMemo(() => {
    if (!selectedItemType || data.length === 0) {
      return [];
    }

    const selectedLabel = selectedItemType.label || "";

    // Filter data by selected item type
    let filtered = data.filter((item) => item.item_type === selectedLabel);

    // Apply time filter if cutoff date exists
    if (cutoffDate) {
      filtered = filtered.filter((item) => {
        const itemDate = parseISOWeek(item["year-week"]);
        return itemDate && itemDate >= cutoffDate;
      });
    }

    // Sort by year-week to ensure chronological order
    return filtered.sort((a, b) => {
      const weekA = a["year-week"] || "";
      const weekB = b["year-week"] || "";
      return weekA.localeCompare(weekB);
    });
  }, [selectedItemType, data, cutoffDate, parseISOWeek]);

  const handleItemTypeChange = (event, newValue) => {
    if (newValue === null && itemTypes.length > 0) {
      setSelectedItemType(itemTypes[0]);
      onFilterChange({
        item: itemTypes[0].label,
        time: timeFilter.value.toLowerCase(),
      });
    } else {
      setSelectedItemType(newValue);
      onFilterChange({
        item: newValue?.label,
        time: timeFilter.value.toLowerCase(),
      });
    }
  };

  const handleTimeFilterChange = (event, newValue) => {
    if (newValue !== null) {
      setTimeFilter(newValue);
      onFilterChange({
        item: selectedItemType?.label,
        time: newValue.value.toLowerCase(),
      });
    }
  };

  // Format week label for display (YYYY-WNN -> WNN)
  const formatWeekLabel = (yearWeek) => {
    if (!yearWeek) return "";
    const parts = yearWeek.split("-");
    if (parts.length >= 2) {
      return parts[1]; // Returns "W26" from "2025-W26"
    }
    return yearWeek;
  };

  // Format large numbers compactly (e.g., 260k, 1.2M)
  // Decimals only shown if value < 10
  const formatCompactNumber = (value) => {
    if (value >= 1000000) {
      const millions = value / 1000000;
      return millions < 10
        ? `${millions.toFixed(1)}M`
        : `${millions.toFixed(0)}M`;
    }
    if (value >= 1000) {
      const thousands = value / 1000;
      return thousands < 10
        ? `${thousands.toFixed(1)}k`
        : `${thousands.toFixed(0)}k`;
    }
    return value.toFixed(0);
  };

  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "200px",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          p: 3,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          color: "error.main",
        }}
      >
        {error}
      </Box>
    );
  }

  // Customize the tooltip
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const dataPoint = payload[0]?.payload;

      // Check if this is a week with no data (null values)
      const hasData = dataPoint?.total_transaction_value !== null;

      return (
        <Card sx={{ p: 1.5, boxShadow: 2, bgcolor: "background.paper" }}>
          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
            {dataPoint?.["year-week"] || label}
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ mb: 1, display: "block" }}
          >
            {dataPoint?.week_start} to {dataPoint?.week_end}
          </Typography>
          {hasData ? (
            <>
              {payload
                .filter((entry) => entry.value !== null)
                .map((entry, index) => (
                  <Box key={index} sx={{ mb: 0.5 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        color: entry.color,
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      <Box
                        sx={{
                          width: 12,
                          height: 12,
                          bgcolor: entry.color,
                          mr: 1,
                          borderRadius: 0.5,
                        }}
                      />
                      {entry.name}:{" "}
                      {typeof entry.value === "number"
                        ? entry.value.toLocaleString()
                        : entry.value}
                    </Typography>
                  </Box>
                ))}
              {/* Additional info - Qty Sold */}
              <Box
                sx={{
                  mt: 1,
                  pt: 1,
                  borderTop: "1px solid",
                  borderColor: "divider",
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  Qty Sold: {dataPoint.total_sales?.toLocaleString() || 0}
                </Typography>
              </Box>
            </>
          ) : (
            <Typography variant="body2" color="text.secondary">
              No data for this week
            </Typography>
          )}
        </Card>
      );
    }
    return null;
  };

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
        <DateRangeIcon sx={{ mr: 1, color: theme.palette.primary.main }} />
        <Typography variant="h5">{title}</Typography>
      </Box>
      {subtitle && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {subtitle}
        </Typography>
      )}

      <Box sx={{ mb: 3 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={4}>
            <Autocomplete
              id="item-type-filter-weekly"
              options={itemTypes}
              value={selectedItemType}
              onChange={handleItemTypeChange}
              getOptionLabel={(option) => option.label || ""}
              isOptionEqualToValue={(option, value) =>
                option.label === value.label
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Item Type"
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <>
                        <FilterListIcon
                          sx={{ color: "action.active", mr: 1 }}
                        />
                        {params.InputProps.startAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              select
              label="Time Period"
              value={timeFilter.value}
              onChange={(e) => {
                const selected = TIME_FILTER_OPTIONS.find(
                  (opt) => opt.value === e.target.value,
                );
                handleTimeFilterChange(null, selected);
              }}
              sx={{ minWidth: 200 }}
              InputProps={{
                startAdornment: (
                  <CalendarMonthIcon
                    sx={{ color: "action.active", mr: 1, ml: 1 }}
                  />
                ),
              }}
            >
              {TIME_FILTER_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid
            item
            xs={12}
            md={5}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              flexWrap: "wrap",
            }}
          >
            {selectedItemType && (
              <Chip
                label={selectedItemType.label}
                color="primary"
                variant="outlined"
                size="medium"
              />
            )}
            <Chip
              label={timeFilter.label}
              color="secondary"
              variant="outlined"
              size="medium"
            />
          </Grid>
        </Grid>
      </Box>

      {filteredData.length > 0 ? (
        <>
          <ResponsiveContainer width="100%" height={isMobile ? 350 : 400}>
            <ComposedChart
              data={filteredData}
              margin={{
                top: 20,
                right: isMobile ? 5 : 30,
                left: isMobile ? 5 : 20,
                bottom: 10,
              }}
            >
              <CartesianGrid strokeDasharray="3 3" strokeOpacity={0.6} />
              <XAxis
                dataKey="year-week"
                tick={{ fill: theme.palette.text.primary, fontSize: 11 }}
                tickLine={{ stroke: theme.palette.divider }}
                axisLine={{ stroke: theme.palette.divider }}
                tickFormatter={formatWeekLabel}
                angle={-45}
                textAnchor="end"
                height={60}
              />
              <YAxis
                yAxisId="left"
                orientation="left"
                tick={{
                  fill: theme.palette.text.primary,
                  fontSize: isMobile ? 10 : 12,
                }}
                tickLine={{ stroke: theme.palette.divider }}
                axisLine={{ stroke: theme.palette.divider }}
                tickFormatter={formatCompactNumber}
                label={{
                  value: "Total Sales $",
                  angle: -90,
                  position: "insideLeft",
                  style: {
                    textAnchor: "middle",
                    fill: theme.palette.text.secondary,
                    fontSize: isMobile ? 10 : 12,
                  },
                }}
              />
              <YAxis
                yAxisId="right"
                orientation="right"
                tick={{
                  fill: theme.palette.text.primary,
                  fontSize: isMobile ? 10 : 12,
                }}
                tickLine={{ stroke: theme.palette.divider }}
                axisLine={{ stroke: theme.palette.divider }}
                tickFormatter={formatCompactNumber}
                label={{
                  value: "Avg Price",
                  angle: 90,
                  position: "insideRight",
                  style: {
                    textAnchor: "middle",
                    fill: theme.palette.text.secondary,
                    fontSize: isMobile ? 10 : 12,
                  },
                }}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend
                wrapperStyle={{
                  paddingTop: isMobile ? "10px" : "20px",
                  fontSize: isMobile ? "12px" : "14px",
                }}
                iconSize={isMobile ? 12 : 14}
                height={isMobile ? 40 : 36}
              />
              <Bar
                dataKey="total_transaction_value"
                name="Total Sales $"
                yAxisId="left"
                fill={alpha(theme.palette.primary.main, 0.7)}
                radius={[4, 4, 0, 0]}
              />
              <Line
                type="monotone"
                dataKey="avg_price_per_unit"
                name="Avg Price"
                yAxisId="right"
                stroke={theme.palette.secondary.main}
                strokeWidth={2}
                dot={{ r: 3, fill: theme.palette.secondary.main }}
                activeDot={{ r: 5 }}
                connectNulls={true}
              />
            </ComposedChart>
          </ResponsiveContainer>

          {/* Volume Mini-Chart */}
          <Box sx={{ mt: 3 }}>
            <ResponsiveContainer width="100%" height={isMobile ? 80 : 120}>
              <ComposedChart
                data={filteredData}
                margin={{
                  top: 5,
                  right: isMobile ? 5 : 30,
                  left: isMobile ? 5 : 20,
                  bottom: 5,
                }}
              >
                <CartesianGrid strokeDasharray="3 3" strokeOpacity={0.3} />
                <XAxis
                  dataKey="year-week"
                  tickFormatter={formatWeekLabel}
                  tick={{
                    fill: theme.palette.text.primary,
                    fontSize: 10,
                  }}
                  tickLine={{ stroke: theme.palette.divider }}
                  axisLine={{ stroke: theme.palette.divider }}
                  angle={-45}
                  textAnchor="end"
                  height={40}
                />
                <YAxis
                  yAxisId="left"
                  orientation="left"
                  tickFormatter={formatCompactNumber}
                  tick={{
                    fill: theme.palette.text.primary,
                    fontSize: 10,
                  }}
                  tickLine={{ stroke: theme.palette.divider }}
                  axisLine={{ stroke: theme.palette.divider }}
                />
                <YAxis
                  yAxisId="right"
                  orientation="right"
                  tick={false}
                  tickLine={false}
                  axisLine={false}
                />
                <Tooltip
                  cursor={{ fill: "rgba(0,0,0,0.1)" }}
                  content={({ active, payload, label }) => {
                    if (active && payload && payload.length) {
                      const dataPoint = payload[0]?.payload;
                      return (
                        <Card
                          sx={{
                            p: 1,
                            boxShadow: 2,
                            bgcolor: "background.paper",
                          }}
                        >
                          <Typography
                            variant="caption"
                            sx={{ display: "block", mb: 0.5 }}
                          >
                            {dataPoint?.["year-week"] || label}
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{ color: theme.palette.success.main }}
                          >
                            <strong>Volume:</strong>{" "}
                            {dataPoint?.total_sales?.toLocaleString() || 0}{" "}
                            units
                          </Typography>
                        </Card>
                      );
                    }
                    return null;
                  }}
                />
                <Bar
                  dataKey="total_sales"
                  name="Volume (Qty Sold)"
                  yAxisId="left"
                  fill={alpha(theme.palette.success.main, 0.7)}
                  radius={[2, 2, 0, 0]}
                />
                <Legend
                  wrapperStyle={{
                    paddingTop: "10px",
                  }}
                  iconSize={isMobile ? 10 : 14}
                />
              </ComposedChart>
            </ResponsiveContainer>
          </Box>
        </>
      ) : (
        <Box
          sx={{
            height: 400,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: alpha(theme.palette.primary.main, 0.03),
            borderRadius: 1,
          }}
        >
          <Typography variant="subtitle1" color="text.secondary">
            No weekly data available for the selected item type
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default MarketsDataByWeek;
